import type { Client, ClientHeaders } from "@medusajs/js-sdk";

import type { SalonStockerHttpTypes } from "@salonstocker/framework";

export interface StoreToggleFavoriteResponse {
  favorited: boolean;
  list_id: string;
}

/**
 * Client for the buyer **Favorites** endpoint (`/store/favorites`, KAN-14).
 *
 * Favorites is a `kind: "favorites"` saved list under the hood (api ADR-0002) -
 * once one exists, `sdk.store.savedList` already covers view/rename/delete/
 * add-to-cart on it unmodified. This client is only the two favorites-specific
 * entry points: a cheap membership fetch and the single-tap toggle.
 */
export class Favorites {
  private client: Client;

  constructor(client: Client) {
    this.client = client;
  }

  /**
   * The salon's favourited `(seller_id, variant_id)` keys - cheap enough to
   * fetch once and check membership client-side for every heart icon on
   * screen, instead of loading the full saved list.
   */
  list = async (
    headers?: ClientHeaders
  ): Promise<SalonStockerHttpTypes.StoreFavorites> =>
    this.client.fetch<SalonStockerHttpTypes.StoreFavorites>(
      "/store/favorites",
      { method: "GET", headers }
    );

  /**
   * Flip membership of a `(seller_id, variant_id)` line - creates the
   * favorites list on first use.
   */
  toggle = async (
    item: SalonStockerHttpTypes.StoreToggleFavorite,
    headers?: ClientHeaders
  ): Promise<StoreToggleFavoriteResponse> =>
    this.client.fetch<StoreToggleFavoriteResponse>("/store/favorites/toggle", {
      method: "POST",
      body: item,
      headers
    });
}
